import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import '../charts/ChartRegistry'; // Import to ensure chart components are registered
import { mathChartOptions, equationColors } from '../charts/ChartRegistry';
import { MathRenderer } from './MathRenderer';
import { EquationChartProps, ChartEquationData } from '../../types';


/**
 * EquationChart Component
 *
 * A sophisticated mathematical function plotting component that:
 * - Renders multiple equations simultaneously with distinct colors
 * - Generates smooth curves using configurable resolution
 * - Supports LaTeX rendering in labels and legends
 * - Provides responsive design matching the existing chart patterns
 * - Auto-calculates optimal axis ranges when not specified
 * - Handles mathematical edge cases (infinity, undefined values)
 *
 * @example
 * ```tsx
 * const equations: EquationData[] = [
 *   {
 *     id: 'quadratic',
 *     expression: 'x^2',
 *     evaluate: (x) => x * x,
 *   },
 *   {
 *     id: 'linear',
 *     expression: '2x + 1',
 *     evaluate: (x) => 2 * x + 1,
 *   }
 * ];
 *
 * <EquationChart
 *   equations={equations}
 *   title="Mathematical Functions"
 *   xRange={{ min: -5, max: 5 }}
 * />
 * ```
 */
export const EquationChart: React.FC<EquationChartProps> = ({
  equations,
  xRange = { min: -5, max: 5, step: 0.1 },
  yRange,
  resolution = 100,
  customOptions,
  showLegend = true,
  showAxisLabels = true,
  points = [],
}) => {
  // Generate data points for all equations
  const chartData = useMemo(() => {

    // Calculate x values based on range and resolution
    const xStep = (xRange.max - xRange.min) / resolution;
    const xValues = Array.from(
      { length: resolution + 1 },
      (_, i) => xRange.min + i * xStep
    );

    // Generate datasets for each visible equation
    const datasets = equations
      .filter(eq => eq.visible !== false)
      .map((equation, index) => {

        // Use explicit color if provided, otherwise use color index system
        let colors;
        if (equation.color) {
          // Use explicit color
          colors = {
            borderColor: equation.color,
            backgroundColor: equation.color,
          };
        } else {
          // Fallback to color index system
          const colorIndex = equation.colorIndex ?? index % equationColors.length;
          colors = equationColors[colorIndex];
        }

        // Test the evaluate function first
        try {
          const testEval = equation.evaluate(1);
        } catch (error) {
          console.error(`🚨 Error in test evaluation:`, error);
        }

        // Calculate y values, handling mathematical edge cases
        const dataPoints = xValues.map(x => {
          try {
            const y = equation.evaluate(x);
            // Filter out invalid values (NaN, Infinity)
            if (!isFinite(y)) {
              return null;
            }
            return { x, y };
          } catch (error) {
            // Handle evaluation errors gracefully
            console.error(`🚨 Evaluation error at x=${x}:`, error);
            return null;
          }
        }).filter(point => point !== null) as Array<{ x: number; y: number }>;

        return {
          label: equation.label || equation.equation,
          data: dataPoints,
          ...colors,
          borderWidth: 2,
          fill: false,
          tension: 0.1,
          pointRadius: 0, // Hide points for smoother curves
          pointHoverRadius: 4,
          spanGaps: false, // Don't connect across null values
        };
      });

    // Add point datasets (styled as line datasets with single points)
    const pointDatasets = points.map((point, index) => ({
      label: point.label,
      data: [{ x: point.x, y: point.y }],
      backgroundColor: point.color,
      borderColor: point.color,
      borderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      showLine: false, // Don't connect points with lines
      fill: false,
      tension: 0,
    }));

    return {
      datasets: [...datasets, ...pointDatasets],
    };
  }, [equations, xRange, resolution, points]);

  // Calculate optimal y-range if not provided
  const calculatedYRange = useMemo(() => {
    if (yRange) return yRange;

    // Find min and max y values from all datasets
    let minY = Infinity;
    let maxY = -Infinity;

    chartData.datasets.forEach(dataset => {
      dataset.data.forEach(point => {
        if (typeof point.y === 'number' && isFinite(point.y)) {
          minY = Math.min(minY, point.y);
          maxY = Math.max(maxY, point.y);
        }
      });
    });

    // Also include standalone points in Y range calculation
    points.forEach(point => {
      if (typeof point.y === 'number' && isFinite(point.y)) {
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      }
    });

    // Include the origin so supplied points can be read on a Cartesian plane.
    minY = Math.min(minY, 0);
    maxY = Math.max(maxY, 0);

    // Add 10% padding and ensure reasonable bounds
    if (isFinite(minY) && isFinite(maxY)) {
      const range = maxY - minY;
      const padding = Math.max(range * 0.1, 1);
      return {
        min: minY - padding,
        max: maxY + padding,
      };
    }

    // Fallback range if no valid data
    return { min: -10, max: 10 };
  }, [chartData, yRange, points]);

  // Chart options with LaTeX support
  const options = useMemo(() => ({
    ...mathChartOptions,
    // Keep supplied coordinates stationary while students read the graph.
    animation: false as const,
    ...customOptions,
    plugins: {
      ...mathChartOptions.plugins,
      legend: {
        ...mathChartOptions.plugins?.legend,
        display: showLegend,
      },
      tooltip: {
        ...mathChartOptions.plugins?.tooltip,
        callbacks: {
          title: function (context: any) {
            const point = context[0];
            return `x = ${point.parsed.x.toFixed(3)}`;
          },
          label: function (context: any) {
            return `${context.dataset.label}: y = ${context.parsed.y.toFixed(3)}`;
          },
        },
      },
    },
    scales: {
      ...mathChartOptions.scales,
      x: {
        ...mathChartOptions.scales?.x,
        min: xRange.min,
        max: xRange.max,
        title: {
          display: showAxisLabels,
          text: 'x',
          font: {
            size: 16,
            weight: 'bold' as const,
          },
        },
      },
      y: {
        ...mathChartOptions.scales?.y,
        min: calculatedYRange.min,
        max: calculatedYRange.max,
        title: {
          display: showAxisLabels,
          text: 'y',
          font: {
            size: 16,
            weight: 'bold' as const,
          },
        },
      },
    },
  }), [xRange, calculatedYRange, showLegend, showAxisLabels, customOptions]);

  // Show message if no equations are provided or visible
  if (points.length === 0 && (!equations.length || equations.every(eq => eq.visible === false))) {
    return (
      <div className={`bg-white p-4 rounded-lg shadow-md h-full`}>
        <div className="h-[400px] flex items-center justify-center bg-gray-50 rounded border-2 border-dashed border-gray-300">
          <div className="text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-sm font-medium">No equations to display</p>
            <p className="text-xs">Add equations to see the mathematical plot</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white p-4 rounded-lg shadow-md h-full`}>

      <div className="h-[400px]">
        <Line data={chartData} options={options} />
      </div>

      {/* Equation legend with LaTeX rendering */}
      {equations.some(eq => eq.visible !== false) && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex flex-wrap flex-col gap-3">
            {equations
              .filter(eq => eq.visible !== false)
              .map((equation, index) => {
                // Use explicit color if provided, otherwise use color index system
                const color = equation.color || equationColors[equation.colorIndex ?? index % equationColors.length].borderColor;

                return (
                  <div key={equation.id} className="flex items-center gap-2">
                    <div
                      className="w-4 h-0.5 rounded"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-sm">
                      <MathRenderer
                        math={equation.label || equation.equation}
                        display="inline"
                      />
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

// Don't delete these yet

/**
 * Utility function to create simple polynomial equations
 *
 * @param coefficients Array of coefficients [a, b, c] for ax² + bx + c
 * @returns EquationData object ready for use in EquationChart
 */
export const createPolynomialEquation = (
  coefficients: number[],
  id: string,
  label?: string
): ChartEquationData => {
  const [a = 0, b = 0, c = 0] = coefficients;

  // Generate LaTeX expression
  const terms: string[] = [];
  if (a !== 0) {
    if (a === 1) terms.push('x^2');
    else if (a === -1) terms.push('-x^2');
    else terms.push(`${a}x^2`);
  }
  if (b !== 0) {
    if (b === 1 && terms.length > 0) terms.push('+x');
    else if (b === -1) terms.push('-x');
    else if (b > 0 && terms.length > 0) terms.push(`+${b}x`);
    else terms.push(`${b}x`);
  }
  if (c !== 0) {
    if (c > 0 && terms.length > 0) terms.push(`+${c}`);
    else terms.push(`${c}`);
  }

  const equation = terms.length > 0 ? terms.join('') : '0';

  return {
    id,
    equation,
    label,
    evaluate: (x: number) => a * x * x + b * x + c,
  };
};

/**
 * Utility function to create trigonometric equations
 *
 * @param type The trigonometric function type
 * @param amplitude Amplitude of the function (default: 1)
 * @param frequency Frequency of the function (default: 1)
 * @param phase Phase shift in radians (default: 0)
 * @param vertical Vertical shift (default: 0)
 * @returns EquationData object ready for use in EquationChart
 */
export const createTrigEquation = (
  type: 'sin' | 'cos' | 'tan',
  amplitude: number = 1,
  frequency: number = 1,
  phase: number = 0,
  vertical: number = 0,
  id: string,
  label?: string
): ChartEquationData => {
  // Generate LaTeX expression
  const amplitudeStr = amplitude === 1 ? '' : `${amplitude}`;
  const frequencyStr = frequency === 1 ? '' : `${frequency}`;
  const phaseStr = phase === 0 ? '' : `${phase > 0 ? '+' : ''}${phase}`;
  const verticalStr = vertical === 0 ? '' : `${vertical > 0 ? '+' : ''}${vertical}`;

  const equation = `${amplitudeStr}\\${type}(${frequencyStr}x${phaseStr})${verticalStr}`;

  const evaluate = (x: number) => {
    const angle = frequency * x + phase;
    let result: number;

    switch (type) {
      case 'sin':
        result = amplitude * Math.sin(angle) + vertical;
        break;
      case 'cos':
        result = amplitude * Math.cos(angle) + vertical;
        break;
      case 'tan':
        result = amplitude * Math.tan(angle) + vertical;
        break;
      default:
        result = 0;
    }

    return result;
  };

  return {
    id,
    equation,
    label,
    evaluate,
  };
};